// 메신저 입력창에 엑셀 표를 붙여넣을 때 «데이터로 / 이미지로» 를 고르게 한다.
// 설계: `patch_mm_excel_paste_choice.md`(마스터 2026-10-06 «엑셀 내용 복붙할때 이미지로 붙여넣기,
// 데이터로 붙여넣기 선택할수 있게 해주고 … 이미지로 붙여넣기 선택하면 이미지로 변환해서 붙여넣기»).
//
// 무엇을 하는가
//   ① 가운데(`#post_textbox`)·스레드(`#reply_textbox`) 입력창의 paste 를 **capture 단계**에서 먼저 받아
//      엑셀 표(html 에 `<table` + 엑셀/시트 표식)면 MM 기본 처리를 막고 작은 선택창을 띄운다.
//   ② [데이터로] = 복사해 둔 html·plain 을 **그대로 다시** 같은 입력창에 paste 로 쏜다 → MM 의 표 변환기가
//      지금과 똑같이 마크다운 표를 넣는다(동작 무변경).
//   ③ [이미지로] = 표를 PNG 로 만들어 «이미지 파일만 든» paste 로 쏜다 → MM FileUpload 가 첨부 대기열에
//      올린다(게시는 사용자가 [보내기]로 한다).
//
// 🔴 MM DOM·MM 이벤트 처리는 MM 소유다 — 어긋나면 전부 조용히 무동작(= 지금 동작으로 돌아간다).
// 🔴 재발송 이벤트는 모듈 WeakSet 으로 표시해 **우리 리스너가 다시 가로채지 않게** 한다.
// 🔴 MM 의 표 변환기는 `text/html`·`text/plain` 만 읽고, FileUpload 는 **text/plain 이 비어 있지 않으면
//    파일을 올리지 않는다** → 이미지 재발송 DataTransfer 에는 text 를 절대 넣지 않는다(설계 §1 ②).
// 🔴 `DataTransfer`·`ClipboardEvent`·`File`·`Image`·`DOMParser`·`XMLSerializer` 는 전부
//    `doc.defaultView`(iframe 쪽) 생성자를 쓴다 — 최상위 창 생성자는 iframe 안 검사에서 어긋날 수 있다
//    (선례 주석: `mmMenuBackClose.js:12-13` · `mmPreviewTouch.js:144-145`). 대상 판정도 `instanceof` 가
//    아니라 `tagName` + id 로 한다.
// 🔴 클립보드는 핸들러가 끝나면 비워진다 → html·plain·이미지 파일을 **동기로** 먼저 복사해 둔다.
export const MENU_ID = 'gw-excel-paste-menu';
export const WRAP_ID = 'gw-excel-paste-wrap';       // 측정·그리기 공용 감싸개(같은 상자여야 크기가 맞는다)
export const MEASURE_ATTR = 'data-gw-excel-measure';
export const ACT_ATTR = 'data-gw-act';              // 버튼 구분(data|image|close)
export const TARGET_IDS = ['post_textbox', 'reply_textbox'];
export const PLAIN_PASTE_MS = 250;                  // Ctrl/⌘+Shift+V 직후 통과 시한(MM 과 같게)
export const MAX_HTML_LEN = 2000000;                // 거대 범위는 판정 전에 통과(지금 동작 그대로)
export const MAX_CANVAS_SIDE = 8000;
export const MAX_CANVAS_AREA = 16000000;
export const RENDER_TIMEOUT_MS = 10000;             // 렌더 전체 상한(측정 iframe load 포함)
export const FILE_PREFIX = 'excel_';                // 🔴 'sticker_' 로 시작하면 채널 파일 목록에서 숨는다
export const ALLOWED_TAGS = ['table', 'thead', 'tbody', 'tfoot', 'tr', 'td', 'th', 'col', 'colgroup',
  'caption', 'br', 'span', 'font', 'b', 'i', 'u', 's', 'sub', 'sup', 'div', 'p'];
export const ALLOWED_ATTRS = ['class', 'style', 'colspan', 'rowspan', 'span', 'width', 'height',
  'align', 'valign', 'bgcolor', 'nowrap', 'color', 'face', 'size'];
export const DROP_TAGS = ['script', 'style', 'template', 'noscript'];   // 내용째 지운다
// 🔴 격자선 기본 CSS 는 **엑셀 `<style>` 뒤에** 요소 선택자로만 둔다 — 엑셀 html 의 `td{…border:none…}`
//    기본값은 같은 특이도라 뒤에 둔 우리 것이 이기고, 엑셀이 셀 클래스(.xlNN)로 지정한 테두리·배경·글꼴은
//    특이도가 높아 그대로 이긴다(화면 격자처럼 보이면서 사용자 서식은 유지).
export const BASE_CSS = 'table{border-collapse:collapse;background:#fff}'
  + " td,th{border:1px solid #d0d7de;padding:2px 6px;white-space:nowrap;font-family:'Malgun Gothic',sans-serif}";
export const MEASURE_CSP = "default-src 'none'; style-src 'unsafe-inline'; img-src data:";
// 화면 밖·투명이지만 «크기 0 은 아니다»(0 이면 레이아웃이 접혀 표 크기를 못 읽는다).
export const MEASURE_FRAME_STYLE = 'position:fixed;left:-20000px;top:0;width:4000px;height:4000px;'
  + 'border:0;opacity:0;pointer-events:none';
const BAD_CSS = /url\(|@import|expression\(/i;
const SVG_NS = 'http://www.w3.org/2000/svg';
const XHTML_NS = 'http://www.w3.org/1999/xhtml';
const MENU_EST_H = 92;      // rect 를 못 읽을 때 쓰는 선택창 높이 추정(위치 계산용)

// 우리가 다시 쏜 paste — 같은 리스너가 또 가로채지 않게 표시해 둔다.
const RESENT = (typeof WeakSet === 'function') ? new WeakSet() : null;

function viewOf(doc) {
  try { return (doc && doc.defaultView) || null; } catch (e) { return null; }
}

function prevent(ev) {
  try { if (ev && ev.cancelable !== false && typeof ev.preventDefault === 'function') ev.preventDefault(); } catch (e) { /* */ }
}

function markResent(ev) {
  try { if (RESENT && ev) RESENT.add(ev); } catch (e) { /* */ }
}

function isResent(ev) {
  try { return !!(RESENT && ev && RESENT.has(ev)); } catch (e) { return false; }
}

function readData(cd, type) {
  try { const v = cd.getData(type); return typeof v === 'string' ? v : ''; } catch (e) { return ''; }
}

// 클립보드에 함께 들어온 «그림» 한 장(윈도 데스크톱 엑셀은 표 그림도 같이 넣는다).
export function imageFromClipboard(cd) {
  try {
    const items = cd && cd.items;
    const n = (items && items.length) || 0;
    for (let i = 0; i < n; i += 1) {
      const it = items[i];
      if (!it || it.kind !== 'file') continue;
      if (String(it.type || '').toLowerCase().indexOf('image/') !== 0) continue;
      const f = typeof it.getAsFile === 'function' ? it.getAsFile() : null;
      if (f) return f;
    }
  } catch (e) { /* */ }
  return null;
}

// «엑셀에서 복사한 표» 판정 — `<table` + (엑셀 네임스페이스 | ProgId Excel.Sheet | 구글 시트 표식).
// 🔴 이 판정이 거짓이면 아무것도 막지 않는다(기존 붙여넣기 동작 무변경).
export function isExcelHtml(html) {
  if (typeof html !== 'string' || !html) return false;
  const low = html.toLowerCase();
  if (low.indexOf('<table') < 0) return false;
  if (low.indexOf('urn:schemas-microsoft-com:office:excel') >= 0) return true;
  if (low.indexOf('progid') >= 0 && low.indexOf('excel.sheet') >= 0) return true;
  return low.indexOf('google-sheets-html-origin') >= 0;
}

// `style` 속성 정화 — `url(`·`@import`·`expression(` 이 든 **선언만** 버린다.
export function sanitizeDeclList(value) {
  return String(value === undefined || value === null ? '' : value)
    .split(';')
    .map((d) => d.trim())
    .filter((d) => d && !BAD_CSS.test(d))
    .join('; ');
}

// `<style>` 텍스트 정화. 반환값에는 `url(`·`@import`·`expression(`·`<`·`>`·`&` 가 **절대 남지 않는다**
// (남으면 통째로 버린다 — 그래도 격자선 기본 CSS 로 그려진다).
// 🔴 `<`·`>`·`&` 를 지우는 이유: 이 텍스트는 `<style>` 안에 **그대로** 들어가고(srcdoc·SVG),
//    SVG 쪽은 XML 파서가 읽으므로 `&` 하나로 그림 전체가 안 그려진다.
export function sanitizeCss(css) {
  let s = String(css === undefined || css === null ? '' : css);
  s = s.replace(/\/\*[\s\S]*?\*\//g, ' ');                    // 주석(검사 회피) 제거
  s = s.replace(/[<>&]/g, ' ');                               // 담을 곳(<style>·SVG)을 깨지 못하게
  s = s.replace(/@import[^;{]*(;|$)/gi, ' ');                 // @import 통째로
  s = s.replace(/\{([^{}]*)\}/g, (m, body) => '{' + sanitizeDeclList(body) + '}');
  if (BAD_CSS.test(s)) {
    s = s.replace(/[^{}]*\{[^{}]*\}/g, (rule) => (BAD_CSS.test(rule) ? ' ' : rule));
  }
  return BAD_CSS.test(s) ? '' : s;
}

function tagOf(el) {
  try { return String(el.tagName || '').toLowerCase(); } catch (e) { return ''; }
}

function dropNode(node) {
  try { if (node && node.parentNode) node.parentNode.removeChild(node); } catch (e) { /* */ }
}

// 허용 밖 요소는 제거하되 **자식은 남긴다**(셀 글자가 사라지지 않게). 반환 = 옮겨진 자식들.
function unwrapNode(node) {
  const moved = [];
  try {
    const parent = node.parentNode;
    if (!parent) return moved;
    while (node.firstChild) {
      const c = node.firstChild;
      parent.insertBefore(c, node);
      moved.push(c);
    }
    parent.removeChild(node);
  } catch (e) { /* */ }
  return moved;
}

function scrubAttrs(el) {
  try {
    const attrs = el.attributes;
    const names = [];
    for (let i = 0; i < ((attrs && attrs.length) || 0); i += 1) names.push(attrs[i].name);
    for (let i = 0; i < names.length; i += 1) {
      const name = names[i];
      const low = String(name).toLowerCase();
      if (ALLOWED_ATTRS.indexOf(low) < 0) { el.removeAttribute(name); continue; }
      if (low !== 'style') continue;
      const v = sanitizeDeclList(el.getAttribute('style'));
      if (v) el.setAttribute('style', v); else el.removeAttribute('style');
    }
  } catch (e) { /* */ }
}

function scrubChild(node, queue) {
  let type = 0;
  try { type = node.nodeType; } catch (e) { type = 0; }
  if (type === 3) return;                                  // 텍스트는 그대로
  if (type !== 1) { dropNode(node); return; }              // 주석·그 밖 노드
  const tag = tagOf(node);
  if (DROP_TAGS.indexOf(tag) >= 0) { dropNode(node); return; }   // script·style 등은 내용째
  if (ALLOWED_TAGS.indexOf(tag) < 0) {
    const moved = unwrapNode(node);
    for (let i = 0; i < moved.length; i += 1) scrubChild(moved[i], queue);
    return;
  }
  queue.push(node);
}

// 허용 목록만 남긴다(요소·속성 모두). 받은 요소를 **제자리에서** 고친다(버리는 문서의 노드다).
export function scrubTable(table) {
  const queue = [table];
  while (queue.length) {
    const el = queue.shift();
    scrubAttrs(el);
    let kids = [];
    try { kids = Array.prototype.slice.call(el.childNodes); } catch (e) { kids = []; }
    for (let i = 0; i < kids.length; i += 1) scrubChild(kids[i], queue);
  }
  return table;
}

// 엑셀 html → { styleText, tableHtml }(둘 다 정화 완료). 표가 없거나 생성자가 없으면 null.
// 🔴 직렬화는 `XMLSerializer` — SVG `<foreignObject>` 안에 넣을 수 있는 XML 로 받는다.
export function sanitizeTable(html, doc) {
  const view = viewOf(doc);
  const DP = view && view.DOMParser;
  const XS = view && view.XMLSerializer;
  if (!DP || !XS) return null;
  let parsed = null;
  try {
    parsed = new DP().parseFromString(String(html === undefined || html === null ? '' : html), 'text/html');
  } catch (e) { return null; }
  if (!parsed) return null;
  let styleText = '';
  try {
    const sheets = parsed.querySelectorAll('style');
    for (let i = 0; i < sheets.length; i += 1) styleText += ' ' + sanitizeCss(sheets[i].textContent || '');
  } catch (e) { styleText = ''; }
  let table = null;
  try { table = parsed.querySelector('table'); } catch (e) { table = null; }
  if (!table) return null;
  let tableHtml = '';
  try { tableHtml = new XS().serializeToString(scrubTable(table)); } catch (e) { tableHtml = ''; }
  if (!tableHtml) return null;
  return { styleText: styleText.trim(), tableHtml: tableHtml };
}

// 측정용 숨은 iframe 의 문서 — 스크립트 없음(sandbox)·외부 로드 없음(CSP)·여백 0.
export function measureSrcdoc(parts) {
  return '<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="'
    + MEASURE_CSP + '"><style>body{margin:0}</style><style>' + (parts.styleText || '')
    + '</style><style>' + BASE_CSS + '</style></head><body><div id="' + WRAP_ID
    + '" style="display:inline-block">' + parts.tableHtml + '</div></body></html>';
}

// 그리기용 SVG — 측정과 **같은 감싸개(inline-block)·같은 CSS 순서**여야 크기가 맞는다.
export function buildSvg(parts, size) {
  return '<svg xmlns="' + SVG_NS + '" width="' + size.width + '" height="' + size.height + '">'
    + '<foreignObject width="100%" height="100%">'
    + '<div xmlns="' + XHTML_NS + '" id="' + WRAP_ID + '" style="display:inline-block">'
    + '<style>' + (parts.styleText || '') + BASE_CSS + '</style>'
    + parts.tableHtml + '</div></foreignObject></svg>';
}

export function pngName(date) {
  const d = (date && typeof date.getFullYear === 'function') ? date : new Date();
  const p = (n) => (n < 10 ? '0' + n : String(n));
  return FILE_PREFIX + d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate())
    + '_' + p(d.getHours()) + p(d.getMinutes()) + p(d.getSeconds()) + '.png';
}

function pngFile(doc, blob) {
  const view = viewOf(doc);
  const F = view && view.File;
  if (!F || !blob) return null;
  try { return new F([blob], pngName(), { type: 'image/png' }); } catch (e) { return null; }
}

function withTimeout(promise, ms, label) {
  return new Promise((resolve, reject) => {
    let done = false;
    const timer = setTimeout(() => {
      if (done) return;
      done = true;
      reject(new Error(label));
    }, ms);
    promise.then((v) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      resolve(v);
    }, (e) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      reject(e);
    });
  });
}

// 표 크기 측정 — 🔴 MM 실문서에 넣지 않는다(sandbox iframe + CSP). load 가 안 오면 상한에서 끝난다
// (dispose 가 iframe 을 지워도 Promise 가 매달리지 않게).
function measureTable(doc, parts) {
  return new Promise((resolve, reject) => {
    let frame = null;
    let done = false;
    let timer = null;
    const finish = (fn, arg) => {
      if (done) return;
      done = true;
      if (timer) { clearTimeout(timer); timer = null; }
      try { if (frame && frame.parentNode) frame.parentNode.removeChild(frame); } catch (e) { /* */ }
      fn(arg);
    };
    // 🔴 load 가 안 오는 경우(srcdoc 미지원·dispose 로 떼어냄)에도 **iframe 을 반드시 거둔다**.
    timer = setTimeout(() => finish(reject, new Error('표 측정 시간 초과')), RENDER_TIMEOUT_MS);
    try {
      frame = doc.createElement('iframe');
      frame.setAttribute('sandbox', 'allow-same-origin');     // 🔴 allow-scripts 없음
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute(MEASURE_ATTR, '1');
      frame.setAttribute('style', MEASURE_FRAME_STYLE);
      frame.addEventListener('load', () => {
        try {
          const idoc = frame.contentDocument;
          const wrap = idoc && idoc.getElementById(WRAP_ID);
          if (!wrap) { finish(reject, new Error('표 측정 대상 없음')); return; }
          const r = wrap.getBoundingClientRect();
          const width = Math.ceil(r.width);
          const height = Math.ceil(r.height);
          if (!(width > 0 && height > 0)) { finish(reject, new Error('표 크기 0')); return; }
          finish(resolve, { width: width, height: height });
        } catch (e) { finish(reject, e); }
      });
      frame.srcdoc = measureSrcdoc(parts);
      doc.body.appendChild(frame);
    } catch (e) { finish(reject, e); }
  });
}

// SVG(foreignObject) → Image → canvas → PNG Blob. SVG-as-image 는 스크립트·외부 로드가 원래 막힌다.
function drawPng(doc, parts, size) {
  return new Promise((resolve, reject) => {
    const view = viewOf(doc);
    const IMG = view && view.Image;
    if (!IMG) { reject(new Error('Image 생성자 없음')); return; }
    const dpr = (view && view.devicePixelRatio) || 1;
    const scale = Math.min(2, (Number.isFinite(dpr) && dpr > 0) ? dpr : 1);
    const cw = Math.ceil(size.width * scale);
    const ch = Math.ceil(size.height * scale);
    if (cw > MAX_CANVAS_SIDE || ch > MAX_CANVAS_SIDE || cw * ch > MAX_CANVAS_AREA) {
      reject(new Error('표가 너무 큼(' + cw + '×' + ch + ')'));
      return;
    }
    let url = '';
    try {
      url = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(buildSvg(parts, size));
    } catch (e) { reject(e); return; }
    const img = new IMG();
    img.onload = () => {
      try {
        const canvas = doc.createElement('canvas');
        canvas.width = cw;
        canvas.height = ch;
        const ctx = canvas.getContext('2d');
        if (!ctx) { reject(new Error('canvas 2d 없음')); return; }
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, cw, ch);
        ctx.drawImage(img, 0, 0, cw, ch);
        if (typeof canvas.toBlob !== 'function') { reject(new Error('toBlob 없음')); return; }
        canvas.toBlob((blob) => {
          if (!blob) { reject(new Error('PNG 변환 실패')); return; }
          resolve(blob);
        }, 'image/png');
      } catch (e) { reject(e); }
    };
    img.onerror = () => reject(new Error('표 그리기 실패'));
    try { img.src = url; } catch (e) { reject(e); }
  });
}

// 기본 renderTable — 엑셀 html 을 PNG Blob 으로. 시험에서는 주입으로 갈아낀다(jsdom 엔 canvas 가 없다).
export function renderTableToPng(html, doc) {
  const parts = sanitizeTable(html, doc);
  if (!parts) return Promise.reject(new Error('표를 찾지 못했다'));
  const work = measureTable(doc, parts).then((size) => drawPng(doc, parts, size));
  return withTimeout(work, RENDER_TIMEOUT_MS, '표 변환 시간 초과');
}

// ── 선택창 ────────────────────────────────────────────────────────────────
const MENU_STYLE = 'position:fixed;z-index:2147483000;box-sizing:border-box;min-width:208px;'
  + 'padding:8px 10px;background:#fff;border:1px solid #c9ced6;border-radius:8px;'
  + 'box-shadow:0 6px 18px rgba(0,0,0,.18);font-size:13px;line-height:1.35;color:#1b1d21;'
  + "font-family:'Malgun Gothic',sans-serif;text-align:left";
const TITLE_STYLE = 'font-weight:700;margin-bottom:6px';
const MSG_STYLE = 'color:#b3261e;margin-bottom:6px';
const ROW_STYLE = 'display:flex;gap:6px;flex-wrap:wrap';
const HINT_STYLE = 'margin-top:6px;color:#6b7280;font-size:11px';
const BTN_STYLE = 'box-sizing:border-box;padding:5px 10px;border-radius:6px;cursor:pointer;'
  + 'font-size:12px;line-height:1.2;border:1px solid #c9ced6;background:#f4f5f7;color:#1b1d21';
const BTN_PRIMARY = 'box-sizing:border-box;padding:5px 10px;border-radius:6px;cursor:pointer;'
  + 'font-size:12px;line-height:1.2;border:1px solid #1c58d9;background:#1c58d9;color:#fff';

function isCoarsePointer(doc) {
  try {
    const view = viewOf(doc);
    if (!view || typeof view.matchMedia !== 'function') return false;
    const mq = view.matchMedia('(pointer: coarse)');
    return !!(mq && mq.matches);
  } catch (e) { return false; }
}

function focusEl(el) {
  try { if (el && typeof el.focus === 'function') el.focus(); } catch (e) { /* */ }
}

// doc 당 1회 설치. 반환 = dispose(리스너 3개 해제 + 선택창·측정 iframe 제거 + 변환 토큰 무효화).
// 이미 설치돼 있으면 null(가드 관용구는 `mmZoomLabel.js:83·85` 와 같은 모양).
// 🔴 상태는 **doc 별**이다 — 화면 분할이면 메신저 iframe 이 둘이라 전역 상태를 쓰면 서로 간섭한다.
export function setupMmExcelPaste(doc, options) {
  const opts = options || {};
  if (!doc || !doc.body || doc.__gwMmExcelPaste) return null;
  const isActive = typeof opts.isActive === 'function' ? opts.isActive : () => true;
  const renderTable = typeof opts.renderTable === 'function' ? opts.renderTable : renderTableToPng;
  doc.__gwMmExcelPaste = true;
  let disposed = false;
  let open = null;        // 열린 선택창 1개 — { el, box, start, end, data, token, … }
  let token = 0;          // 진행 중 변환 토큰(취소·재시작·dispose 때 늘려 늦은 결과를 버린다)
  let plainAt = 0;        // Ctrl/⌘+Shift+V 를 누른 시각
  let swallowKey = null;  // Enter 처리 뒤 자동반복(repeat)을 keyup 까지 삼킬 키

  const now = () => Date.now();

  function targetBox(t) {
    try {
      if (!t || !t.tagName) return null;
      if (String(t.tagName).toUpperCase() !== 'TEXTAREA') return null;   // 🔴 instanceof 금지(렐름)
      return TARGET_IDS.indexOf(t.id) >= 0 ? t : null;
    } catch (e) { return null; }
  }

  function focusBox(box, start, end) {
    focusEl(box);
    try {
      if (typeof box.setSelectionRange === 'function'
        && typeof start === 'number' && typeof end === 'number') box.setSelectionRange(start, end);
    } catch (e) { /* */ }
  }

  function makeDataTransfer() {
    const view = viewOf(doc);
    const DT = view && view.DataTransfer;
    if (!DT) return null;
    try { return new DT(); } catch (e) { return null; }
  }

  // 같은 입력창에 paste 를 다시 쏜다. 반환 = true(아무도 preventDefault 안 함 = MM 이 처리하지 않음).
  function dispatchPaste(box, dt) {
    const view = viewOf(doc);
    const CE = view && view.ClipboardEvent;
    if (!CE) return false;
    let ev = null;
    try {
      ev = new CE('paste', { clipboardData: dt, bubbles: true, cancelable: true });
    } catch (e) { return false; }
    markResent(ev);
    try { return box.dispatchEvent(ev) === true; } catch (e) { return false; }
  }

  // 합성 paste 에는 «브라우저 기본 삽입» 이 없다 → MM 이 처리하지 않았으면 우리가 넣는다.
  function insertText(text) {
    try {
      if (typeof doc.execCommand !== 'function' || !text) return false;
      return doc.execCommand('insertText', false, text) !== false;
    } catch (e) { return false; }
  }

  function place(el, box) {
    let r = null;
    try { r = box.getBoundingClientRect(); } catch (e) { r = null; }
    if (!r) {
      try { el.style.left = '12px'; el.style.bottom = '12px'; } catch (e) { /* */ }
      return;
    }
    let mw = 0;
    let mh = 0;
    try {
      const mr = el.getBoundingClientRect();
      mw = mr.width;
      mh = mr.height;
    } catch (e) { mw = 0; mh = 0; }
    if (!(mh > 0)) mh = MENU_EST_H;
    if (!(mw > 0)) mw = 208;
    const view = viewOf(doc);
    const vw = (view && view.innerWidth) || 0;
    const top = r.top - mh - 8;
    let left = Math.max(4, r.left);
    if (vw && left + mw > vw - 4) left = Math.max(4, vw - 4 - mw);
    try {
      el.style.top = Math.round(top >= 4 ? top : r.bottom + 8) + 'px';
      el.style.left = Math.round(left) + 'px';
    } catch (e) { /* */ }
  }

  function makeButton(act, label, primary) {
    const b = doc.createElement('button');
    b.type = 'button';
    b.setAttribute(ACT_ATTR, act);
    b.setAttribute('style', primary ? BTN_PRIMARY : BTN_STYLE);
    b.textContent = label;
    b.addEventListener('click', (ev) => {
      prevent(ev);
      try { if (typeof ev.stopPropagation === 'function') ev.stopPropagation(); } catch (e) { /* */ }
      if (disposed || !open) return;
      if (act === 'data') { pasteData(); return; }
      if (act === 'image') { pasteImage(); return; }
      cancelMenu(true);
    });
    return b;
  }

  function focusedAct() {
    try {
      const a = doc.activeElement;
      if (!a || !open || !open.el || !open.el.contains(a)) return null;
      return a.getAttribute(ACT_ATTR);
    } catch (e) { return null; }
  }

  function focusInMenu() {
    try {
      const a = doc.activeElement;
      return !!(a && open && open.el && open.el.contains(a));
    } catch (e) { return false; }
  }

  // «변환 중…»·실패 문구로 바꾼다. 🔴 내용을 바꾸기 **전에** 남는 버튼으로 포커스를 옮긴다 —
  //    포커스된 버튼이 사라질 때의 focusout 으로 선택창이 스스로 닫히지 않게(relatedTarget 규칙과 이중 방어).
  function setMode(mode) {
    const st = open;
    if (!st) return;
    if (focusInMenu()) focusEl(st.dataBtn);
    if (st.imageBtn && st.imageBtn.parentNode) {
      try { st.imageBtn.parentNode.removeChild(st.imageBtn); } catch (e) { /* */ }
    }
    st.imageBtn = null;
    if (mode === 'busy') {
      try { st.hint.textContent = '이미지로 바꾸는 중…'; } catch (e) { /* */ }
      return;
    }
    try { st.msg.textContent = '이미지로 바꾸지 못했습니다'; } catch (e) { /* */ }
    try { st.hint.textContent = 'Esc 취소'; } catch (e) { /* */ }
    if (st.closeBtn) return;
    st.closeBtn = makeButton('close', '닫기', false);
    try { st.row.appendChild(st.closeBtn); } catch (e) { /* */ }
  }

  const onOutside = (ev) => {
    if (disposed || !open) return;
    try {
      const t = ev && ev.target;
      if (t && open.el && typeof open.el.contains === 'function' && open.el.contains(t)) return;
    } catch (e) { /* */ }
    cancelMenu(false);
  };

  const onBoxInput = () => {
    if (disposed || !open) return;
    cancelMenu(false);   // 터치 기기에서 계속 타이핑 등 — 저장한 selection 이 낡는다
  };

  // 🔴 선택창 안 keydown 은 전부 stopPropagation — MM 의 document·window 단축키로 새지 않게.
  const onMenuKey = (ev) => {
    if (disposed || !open) return;
    try { if (typeof ev.stopPropagation === 'function') ev.stopPropagation(); } catch (e) { /* */ }
    let key = '';
    try { key = ev.key; } catch (e) { key = ''; }
    if (key === 'Escape' || key === 'Esc') {
      prevent(ev);
      cancelMenu(true);
      return;
    }
    if (key !== 'Enter') return;
    prevent(ev);
    swallowKey = 'Enter';            // 자동반복 Enter 가 입력창에 닿아 MM 전송이 되지 않게
    if (focusedAct() === 'image') pasteImage();
    else if (focusedAct() === 'close') cancelMenu(true);
    else pasteData();
  };

  // 포커스 이탈 취소 — 🔴 relatedTarget 이 **null 이 아니고** 선택창 밖일 때만(버튼이 사라지거나
  //    비활성화될 때의 focusout 은 relatedTarget 이 null 이라 여기서 걸러진다).
  const onMenuOut = (ev) => {
    if (disposed || !open) return;
    let rel = null;
    try { rel = ev.relatedTarget; } catch (e) { rel = null; }
    if (!rel) return;
    try { if (open.el && open.el.contains(rel)) return; } catch (e) { /* */ }
    cancelMenu(false);
  };

  function bindOpen(st) {
    try { doc.addEventListener('mousedown', onOutside, true); } catch (e) { /* */ }
    try { st.el.addEventListener('keydown', onMenuKey); } catch (e) { /* */ }
    try { st.el.addEventListener('focusout', onMenuOut); } catch (e) { /* */ }
    try { st.box.addEventListener('input', onBoxInput); } catch (e) { /* */ }
  }

  function unbindOpen(st) {
    try { doc.removeEventListener('mousedown', onOutside, true); } catch (e) { /* */ }
    try { st.el.removeEventListener('keydown', onMenuKey); } catch (e) { /* */ }
    try { st.el.removeEventListener('focusout', onMenuOut); } catch (e) { /* */ }
    try { st.box.removeEventListener('input', onBoxInput); } catch (e) { /* */ }
  }

  // 선택창을 닫고 상태를 비운다. 🔴 토큰을 늘려 **진행 중 변환 결과를 버린다**.
  function closeMenu() {
    const st = open;
    open = null;
    token += 1;
    if (!st) return null;
    unbindOpen(st);
    try { if (st.el && st.el.parentNode) st.el.parentNode.removeChild(st.el); } catch (e) { /* */ }
    return st;
  }

  function cancelMenu(restoreFocus) {
    const st = closeMenu();
    if (st && restoreFocus) focusBox(st.box, st.start, st.end);
  }

  function openMenu(box, data) {
    cancelMenu(false);                 // 이미 열려 있으면 취소 처리하고 새로 띄운다
    let start = null;
    let end = null;
    try { start = box.selectionStart; end = box.selectionEnd; } catch (e) { start = null; end = null; }
    const el = doc.createElement('div');
    el.id = MENU_ID;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', '엑셀 붙여넣기');
    el.setAttribute('style', MENU_STYLE);
    const title = doc.createElement('div');
    title.setAttribute('style', TITLE_STYLE);
    title.textContent = '엑셀 붙여넣기';
    const msg = doc.createElement('div');
    msg.setAttribute('style', MSG_STYLE);
    const row = doc.createElement('div');
    row.setAttribute('style', ROW_STYLE);
    const dataBtn = makeButton('data', '데이터로 붙여넣기', true);
    const imageBtn = makeButton('image', '이미지로 붙여넣기', false);
    row.appendChild(dataBtn);
    row.appendChild(imageBtn);
    const hint = doc.createElement('div');
    hint.setAttribute('style', HINT_STYLE);
    hint.textContent = 'Esc 취소';
    el.appendChild(title);
    el.appendChild(msg);
    el.appendChild(row);
    el.appendChild(hint);
    token += 1;
    const st = {
      el: el, box: box, start: start, end: end, data: data, token: token,
      row: row, msg: msg, hint: hint, dataBtn: dataBtn, imageBtn: imageBtn, closeBtn: null,
    };
    open = st;
    try { doc.body.appendChild(el); } catch (e) { open = null; return null; }
    place(el, box);
    bindOpen(st);
    // 🔴 터치 기기는 포커스를 옮기지 않는다 — 소프트 키보드가 닫혀 위치가 어긋난다.
    if (!isCoarsePointer(doc)) focusEl(dataBtn);
    return st;
  }

  // [데이터로] — 복사해 둔 html·plain 을 그대로 다시 쏜다(= 기존 마크다운 표 동작).
  function pasteData() {
    const st = open;
    if (!st) return false;
    const data = st.data;
    closeMenu();
    focusBox(st.box, st.start, st.end);
    const dt = makeDataTransfer();
    if (!dt) return false;
    try { dt.setData('text/html', data.html); } catch (e) { /* */ }
    if (data.plain) { try { dt.setData('text/plain', data.plain); } catch (e) { /* */ } }
    const untouched = dispatchPaste(st.box, dt);
    if (untouched) insertText(data.plain);
    return true;
  }

  // [이미지로] — 클립보드 그림이 있으면 그것을, 없으면 표를 그려 PNG 로. text 는 넣지 않는다.
  function pasteImage() {
    const st = open;
    if (!st) return false;
    const my = st.token;
    const data = st.data;
    setMode('busy');
    let work = null;
    try {
      work = data.file
        ? Promise.resolve(data.file).then((f) => toPng(f))
        : Promise.resolve(renderTable(data.html, doc)).then((blob) => pngFile(doc, blob));
    } catch (e) { work = Promise.reject(e); }
    work.then((file) => {
      if (disposed || !open || open.token !== my) return;    // 취소·dispose·새 paste → 결과를 버린다
      if (!file) { setMode('error'); return; }
      const box = open.box;
      const start = open.start;
      const end = open.end;
      closeMenu();
      focusBox(box, start, end);
      const dt = makeDataTransfer();
      if (!dt || !dt.items || typeof dt.items.add !== 'function') return;
      try { dt.items.add(file); } catch (e) { return; }
      dispatchPaste(box, dt);                                // 🔴 text/html·text/plain 없음(§1 ②)
    }, () => {
      if (disposed || !open || open.token !== my) return;
      setMode('error');
    });
    return true;
  }

  // 클립보드 그림 — PNG 면 이름만 우리 것으로, 아니면 canvas 로 PNG 로 다시 굽는다.
  function toPng(file) {
    if (String((file && file.type) || '').toLowerCase() === 'image/png') {
      const named = pngFile(doc, file);
      if (named) return named;
      throw new Error('File 생성자 없음');
    }
    return bakePng(file);
  }

  function bakePng(file) {
    const view = viewOf(doc);
    const IMG = view && view.Image;
    const URLC = view && view.URL;
    if (!IMG || !URLC || typeof URLC.createObjectURL !== 'function') {
      return Promise.reject(new Error('이미지 변환 불가'));
    }
    return withTimeout(new Promise((resolve, reject) => {
      const url = URLC.createObjectURL(file);
      const img = new IMG();
      const free = () => { try { URLC.revokeObjectURL(url); } catch (e) { /* */ } };
      img.onload = () => {
        try {
          const w = img.naturalWidth || img.width;
          const h = img.naturalHeight || img.height;
          if (!(w > 0 && h > 0)) { free(); reject(new Error('그림 크기 0')); return; }
          if (w > MAX_CANVAS_SIDE || h > MAX_CANVAS_SIDE || w * h > MAX_CANVAS_AREA) {
            free();
            reject(new Error('그림이 너무 큼'));
            return;
          }
          const canvas = doc.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) { free(); reject(new Error('canvas 2d 없음')); return; }
          ctx.drawImage(img, 0, 0);
          free();
          if (typeof canvas.toBlob !== 'function') { reject(new Error('toBlob 없음')); return; }
          canvas.toBlob((blob) => {
            const named = blob ? pngFile(doc, blob) : null;
            if (named) resolve(named); else reject(new Error('PNG 변환 실패'));
          }, 'image/png');
        } catch (e) { free(); reject(e); }
      };
      img.onerror = () => { free(); reject(new Error('그림을 읽지 못했다')); };
      try { img.src = url; } catch (e) { free(); reject(e); }
    }), RENDER_TIMEOUT_MS, '그림 변환 시간 초과');
  }

  const onPaste = (ev) => {
    if (disposed || !ev) return;
    try {
      if (isResent(ev)) return;                       // 우리가 다시 쏜 것 → MM 에게 넘긴다
      if (!isActive()) return;
      const box = targetBox(ev.target);
      if (!box) return;                               // edit_textbox·검색창 등은 손대지 않는다
      if (now() - plainAt < PLAIN_PASTE_MS) return;   // Ctrl/⌘+Shift+V = 서식 없이 붙여넣기
      const cd = ev.clipboardData;
      if (!cd || typeof cd.getData !== 'function') return;
      const html = readData(cd, 'text/html');         // 🔴 동기로 복사(핸들러 뒤엔 비워진다)
      if (!html || html.length > MAX_HTML_LEN) return;
      if (!isExcelHtml(html)) return;
      const plain = readData(cd, 'text/plain');
      const file = imageFromClipboard(cd);
      prevent(ev);
      try { if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation(); } catch (e) { /* */ }
      openMenu(box, { html: html, plain: plain, file: file });
    } catch (e) { /* MM·브라우저 변동 — 무동작 */ }
  };

  const onKeyCapture = (ev) => {
    if (disposed || !ev) return;
    try {
      const key = ev.key;
      if ((ev.ctrlKey || ev.metaKey) && ev.shiftKey && (key === 'v' || key === 'V')) {
        plainAt = now();
        return;
      }
      if (!swallowKey || key !== swallowKey || !ev.repeat) return;
      prevent(ev);
      if (typeof ev.stopImmediatePropagation === 'function') ev.stopImmediatePropagation();
    } catch (e) { /* */ }
  };

  const onKeyUpCapture = (ev) => {
    if (disposed || !ev) return;
    try { if (swallowKey && ev.key === swallowKey) swallowKey = null; } catch (e) { /* */ }
  };

  try { doc.addEventListener('paste', onPaste, true); } catch (e) { /* */ }
  try { doc.addEventListener('keydown', onKeyCapture, true); } catch (e) { /* */ }
  try { doc.addEventListener('keyup', onKeyUpCapture, true); } catch (e) { /* */ }

  return function dispose() {
    if (disposed) return;
    disposed = true;                 // 남은 콜백·늦은 변환 결과는 여기서부터 무동작
    try { doc.removeEventListener('paste', onPaste, true); } catch (e) { /* */ }
    try { doc.removeEventListener('keydown', onKeyCapture, true); } catch (e) { /* */ }
    try { doc.removeEventListener('keyup', onKeyUpCapture, true); } catch (e) { /* */ }
    closeMenu();                     // 선택창 제거 + 변환 토큰 무효화
    try {
      const frames = doc.querySelectorAll('iframe[' + MEASURE_ATTR + ']');
      for (let i = 0; i < frames.length; i += 1) dropNode(frames[i]);
    } catch (e) { /* */ }
    try {
      const menus = doc.querySelectorAll('#' + MENU_ID);
      for (let i = 0; i < menus.length; i += 1) dropNode(menus[i]);
    } catch (e) { /* */ }
    try { doc.__gwMmExcelPaste = false; } catch (e) { /* */ }
  };
}
